/**
 * Auth session helpers: Remember-me persistence + post-login routing.
 *
 * Firebase defaults to local persistence, but Sign-In never honored the
 * Remember me checkbox. These helpers make that explicit and give the
 * router a single place to decide where a restored session should land.
 */
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';

const REMEMBER_PREF_KEY = 'coinproof-remember-me';

/** @returns {boolean} Last Remember-me choice (defaults to true). */
export function getRememberMePreference() {
  try {
    const raw = localStorage.getItem(REMEMBER_PREF_KEY);
    if (raw === null) return true;
    return raw === '1' || raw === 'true';
  } catch (e) {
    return true;
  }
}

/** Persist the checkbox choice for the next Sign-In visit. */
export function saveRememberMePreference(remember) {
  try {
    localStorage.setItem(REMEMBER_PREF_KEY, remember ? '1' : '0');
  } catch (e) { /* ignore */ }
}

/**
 * Apply Firebase Auth persistence before sign-in / register.
 * Remember on  → survive browser / tab close (local).
 * Remember off → clear when the browser session ends.
 *
 * Call this when the Remember-me choice changes (or on Sign-In mount),
 * not immediately before ``signInWithPopup`` — awaiting here breaks the
 * user-gesture and browsers block / cancel the Google popup on first click.
 */
export async function applyRememberMePersistence(remember = true) {
  const auth = getAuth();
  const persistence = remember ? browserLocalPersistence : browserSessionPersistence;
  await setPersistence(auth, persistence);
  saveRememberMePreference(!!remember);
}

/**
 * Wait until Firebase has restored a saved session, or confirmed there is none.
 *
 * `authStateReady()` can resolve while `currentUser` is still null on a hard
 * refresh. The router then sends a signed-in person to /sign-in. The first
 * `onAuthStateChanged` callback is the restore (IndexedDB), so that is the
 * signal we wait for.
 *
 * @returns {Promise<import('firebase/auth').User|null>}
 */
export function waitForAuthUser() {
  const auth = getAuth();
  return new Promise((resolve, reject) => {
    const removeListener = onAuthStateChanged(
      auth,
      (user) => {
        removeListener();
        resolve(user || null);
      },
      (err) => {
        removeListener();
        reject(err);
      },
    );
  });
}

/**
 * Destination after a successful / restored sign-in.
 * @param {import('firebase/auth').User|null|undefined} user
 * @returns {string|null} route path, or null if not signed in
 */
export function postAuthPath(user) {
  if (!user) return null;
  // Google / some providers mark emailVerified true; email/password may not.
  if (user.emailVerified === false) return '/email-verification';
  return '/dashboard/analytics';
}
